/**
 * KRA Photo Journal – PhotoCard
 *
 * Visual card component for a single photo record.
 * Layout:
 *   - Large cover image (portrait ratio) with action icon overlay
 *   - Row of up to 3 small thumbnails
 *   - ChatName (primary label)
 *   - Photographer + return date (metadata)
 *   - Preview status badge
 *
 * Card actions (overlay on cover image, bottom-left):
 *   - Heart / liked toggle
 *   - Import to Drive (opens ImportToDriveModal)
 *
 * Design: Apple-level visual card, soft shadow, rounded corners,
 *         images dominate, metadata supports.
 */

import React, { useCallback, useState, useRef } from "react";
import { motion } from "framer-motion";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { isRequiredTagGroup, getMissingRequiredTagGroups } from "@kra/photo-journal-shared";
import { AnimatePresence } from "framer-motion";
import { LazyImage } from "./LazyImage.js";
import { StatusBadge } from "./StatusBadge.js";
import { TagChip, TagOverflowChip } from "./TagChip.js";
import { getAllTags, TAG_GROUPS } from "../lib/tagHelpers.js";
import { formatDate } from "../lib/utils.js";
import { cn } from "../lib/utils.js";

interface PhotoCardProps {
  photo: PhotoRecord;
  onClick: (photo: PhotoRecord) => void;
  /** Column index for staggered entrance animation */
  index: number;
  onToggleLiked: (photo: PhotoRecord) => void;
  /** Currently logged-in photographer name — used to compute per-photographer like state */
  currentUser?: string | null;
  /** Whether the current session has admin role. */
  isAdmin?: boolean;
  /** Kiosk / read-only mode — hide like/download action buttons */
  isReadOnly?: boolean;
  /** Called when a tag chip is clicked — filter by that tag */
  onTagClick?: (groupKey: string, tagValue: string) => void;
  /** Called when the Import Drive button is clicked */
  onImportDrive?: (photo: PhotoRecord) => void;
  /** Called when the ⚡ Quick Import button is clicked — dumps straight into
   *  the remembered quick folder (set once on first use). */
  onQuickImportDrive?: (photo: PhotoRecord) => void;
  /** Called when admin renames a legacy imported photo */
  onRename?: (photo: PhotoRecord, newName: string) => void;
  /** Called when the Resize button is clicked (admin only, heavy photo sets) */
  onResize?: (photo: PhotoRecord) => void;
  /** Tag options for quick tag editor */
  tagOptions?: Record<string, string[]>;
  /** Called when tags are saved from quick editor */
  onQuickTagSave?: (photo: PhotoRecord, tags: Record<string, string[]>) => void;
  /** Called when the save/bookmark button is clicked (add to collection) */
  onSaveToCollection?: (photo: PhotoRecord) => void;
  /** Multi-select mode: card click toggles selection instead of opening modal */
  selectMode?: boolean;
  /** Whether this card is currently selected in multi-select mode */
  isSelected?: boolean;
  /** Called when the card is clicked while in select mode */
  onToggleSelect?: (photo: PhotoRecord) => void;
}

export function PhotoCard({
  photo,
  onClick,
  index,
  onToggleLiked,
  currentUser,
  isAdmin,
  isReadOnly,
  onTagClick,
  onImportDrive,
  onQuickImportDrive,
  onRename,
  onResize,
  tagOptions,
  onQuickTagSave,
  onSaveToCollection,
  selectMode,
  isSelected,
  onToggleSelect,
}: PhotoCardProps) {
  const { previewURLs, chatName, photographer, returnDate, previewSync, liked, likedBy, adminDriveLink } = photo;
  const [showQuickTags, setShowQuickTags] = useState(false);

  // Per-photographer like: show heart filled if current user has liked this photo
  const isLiked =
    currentUser && !isAdmin
      ? likedBy.includes(currentUser)
      : liked;

  const coverURL = previewURLs?.cover ?? null;
  // Show up to 4 thumbnails below the cover (indices 1–4 of the synced previews).
  // We limit display to 4 so the strip stays compact; the modal shows all 5.
  const thumbURLs = previewURLs?.thumbs?.slice(1, 5) ?? [];
  const hasPreview = previewSync === "Ready" && coverURL !== null;
  const hasAdminLink = Boolean(adminDriveLink?.trim());

  const handleClick = useCallback(() => {
    if (selectMode && onToggleSelect) {
      onToggleSelect(photo);
    } else {
      onClick(photo);
    }
  }, [onClick, photo, selectMode, onToggleSelect]);

  const handleLiked = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleLiked(photo);
    },
    [onToggleLiked, photo]
  );

  const handleImportDrive = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onImportDrive?.(photo);
    },
    [onImportDrive, photo]
  );

  const handleQuickImportDrive = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onQuickImportDrive?.(photo);
    },
    [onQuickImportDrive, photo]
  );

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{
        duration: 0.35,
        delay: Math.min(index * 0.04, 0.4), // stagger capped at 400ms
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
      onClick={handleClick}
      className={cn(
        "group relative flex flex-col cursor-pointer select-none",
        "bg-surface-1 rounded-card shadow-card",
        "transition-shadow duration-250 ease-ios",
        "hover:shadow-card-hover",
        "active:scale-[0.98] active:shadow-card",
        selectMode && isSelected && "ring-2 ring-[#B76E6E] ring-offset-2 ring-offset-surface-0",
      )}
      style={{ WebkitTapHighlightColor: "transparent" }}
      role="button"
      tabIndex={0}
      aria-label={`Open ${chatName}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* ── Cover image ───────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-t-card">
        {hasPreview && coverURL ? (
          <LazyImage
            src={coverURL}
            alt={`${chatName} cover`}
            aspectRatio="3/4"
            className="w-full"
          />
        ) : (
          <NoPreviewPlaceholder status={previewSync} />
        )}

        {/* ── Multi-select checkbox overlay ─────────────────────────────── */}
        {selectMode && (
          <div className="absolute top-2 left-2 z-10 pointer-events-none">
            <div
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors",
                isSelected
                  ? "bg-[#B76E6E] border-white"
                  : "bg-white/70 border-white backdrop-blur-sm",
              )}
              aria-hidden="true"
            >
              {isSelected && (
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          </div>
        )}

        {/* ── Top-right badges ────────────────────────────────────────── */}
        <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
          {/* Drive imported badge (green) */}
          {hasAdminLink && (
            <div className="flex items-center gap-1 rounded-full bg-green-500/85 px-2 py-0.5 backdrop-blur-sm">
              <svg width={8} height={8} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span className="text-[10px] font-semibold text-white leading-none">Drive</span>
            </div>
          )}

          {/* Heavy folder badge (red) — admin only, shows when total >100MB or avg >5MB */}
          {!isReadOnly && photo.originalTotalSize && photo.resizeStatus !== "Done" && (() => {
            const avgSize = photo.originalFileCount
              ? photo.originalTotalSize / photo.originalFileCount
              : 0;
            // "Needs resize" means the folder still holds large ORIGINAL images.
            // Average file size is the reliable signal — a big total made of
            // already-small files (e.g. an album resized out-of-band) is NOT
            // heavy. Fall back to total only when the file count is unknown.
            const isHeavy = photo.originalFileCount
              ? avgSize > 5 * 1024 * 1024
              : photo.originalTotalSize > 100 * 1024 * 1024;
            if (!isHeavy) return null;
            return (
              <button
                onClick={(e) => { e.stopPropagation(); onResize?.(photo); }}
                className="flex items-center gap-1 rounded-full bg-red-500/85 px-2 py-0.5 backdrop-blur-sm hover:bg-red-600/90 transition-colors"
              >
                <span className="text-[10px] font-semibold text-white leading-none">
                  {Math.round(photo.originalTotalSize / 1024 / 1024)} MB
                </span>
              </button>
            );
          })()}

          {/* Resize done badge */}
          {photo.resizeStatus === "Done" && (
            <div className="flex items-center gap-1 rounded-full bg-blue-500/85 px-2 py-0.5 backdrop-blur-sm">
              <span className="text-[10px] font-semibold text-white leading-none">Resized</span>
            </div>
          )}

          {/* Resize in progress badge */}
          {(photo.resizeStatus === "Queued" || photo.resizeStatus === "Resizing") && (
            <button
              onClick={(e) => { e.stopPropagation(); onResize?.(photo); }}
              className="flex items-center gap-1 rounded-full bg-amber-500/85 px-2 py-0.5 backdrop-blur-sm animate-pulse"
            >
              <span className="text-[10px] font-semibold text-white leading-none">Resizing...</span>
            </button>
          )}
        </div>

        {/* ── Card action overlay ──────────────────────────────────────── */}
        <div className="absolute bottom-2 left-2 flex items-center gap-1">
          {/* Heart / Liked — hidden in read-only/kiosk mode */}
          {!isReadOnly && (
            <CardIconBtn
              onClick={handleLiked}
              aria-label={isLiked ? "Unlike" : "Like"}
              active={isLiked}
            >
              <HeartIcon filled={isLiked} />
            </CardIconBtn>
          )}

          {/* Import to Drive — hidden in read-only/kiosk mode */}
          {!isReadOnly && !hasAdminLink && (
            <CardIconBtn
              onClick={handleImportDrive}
              aria-label="Import to Google Drive"
            >
              <ImportIconSmall />
            </CardIconBtn>
          )}

          {/* ⚡ Quick import — dump straight into the remembered quick folder */}
          {!isReadOnly && !hasAdminLink && onQuickImportDrive && (
            <CardIconBtn
              onClick={handleQuickImportDrive}
              aria-label="Import nhanh vào thư mục mặc định"
            >
              <BoltIconSmall />
            </CardIconBtn>
          )}

          {/* Save to Collection — admin only */}
          {isAdmin && onSaveToCollection && (
            <CardIconBtn
              onClick={(e) => { e.stopPropagation(); onSaveToCollection(photo); }}
              aria-label="Thêm vào Collection"
            >
              <BookmarkIcon />
            </CardIconBtn>
          )}
        </div>
      </div>

      {/* ── Thumbnail strip ───────────────────────────────────────────── */}
      {/* Always use a 4-column grid so thumbnails stay the same width
          regardless of how many previews were synced (2, 3, or 4).
          flex-1 caused thumbnails to scale up when fewer were available. */}
      {hasPreview && thumbURLs.length > 0 && (
        <div className="grid grid-cols-4 gap-1 px-2 pt-2">
          {thumbURLs.map((url, i) => (
            <LazyImage
              key={i}
              src={url}
              alt={`${chatName} preview ${i + 2}`}
              aspectRatio="3/4"
              className="rounded-thumb"
            />
          ))}
        </div>
      )}

      {/* ── Metadata ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-1 px-3 py-3">
        {/* Name — double-click to rename for Admin photos */}
        {photographer === "Admin" && onRename ? (
          <InlineRenameTitle
            value={chatName}
            onSave={(newName) => onRename(photo, newName)}
          />
        ) : (
          <h3 className="text-sm font-medium text-ink-0 leading-snug truncate">
            {chatName}
          </h3>
        )}

        {/* Photographer + date row */}
        <div className="flex items-center justify-between gap-2">
          {photographer ? (
            <span className="text-2xs text-ink-2 truncate">{photographer}</span>
          ) : (
            <span className="text-2xs text-ink-3">—</span>
          )}
          {returnDate && (
            <span className="text-2xs text-ink-2 whitespace-nowrap">
              {formatDate(returnDate)}
            </span>
          )}
        </div>

        {/* Status badge – only show if not ready */}
        {previewSync !== "Ready" && (
          <div className="mt-0.5">
            <StatusBadge status={previewSync} />
          </div>
        )}

        {/* Tag chips – show up to 3, then "+N more" */}
        <div className="flex items-center gap-1">
          <div className="flex-1 min-w-0">
            <TagStrip photo={photo} onTagClick={onTagClick} />
          </div>
          {isAdmin && tagOptions && onQuickTagSave && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowQuickTags(true); }}
              className="flex-shrink-0 p-1 rounded-lg text-ink-3 hover:text-[#B76E6E] hover:bg-[#B76E6E]/10 transition-all"
              title="Quick tag"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </button>
          )}
        </div>

        {/* Quick Tag Bottom Sheet */}
        <AnimatePresence>
          {showQuickTags && tagOptions && onQuickTagSave && (
            <QuickTagPopover
              photo={photo}
              tagOptions={tagOptions}
              onSave={(tags) => {
                onQuickTagSave(photo, tags);
                setShowQuickTags(false);
              }}
              onClose={() => setShowQuickTags(false)}
            />
          )}
        </AnimatePresence>
      </div>
    </motion.article>
  );
}

// ── Card icon button ──────────────────────────────────────────────────────────

function CardIconBtn({
  onClick,
  children,
  "aria-label": ariaLabel,
  active,
  disabled,
}: {
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  "aria-label": string;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "w-6 h-6 flex items-center justify-center rounded-full",
        "transition-all duration-150",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1",
        disabled
          ? "bg-black/30 text-white/25 cursor-not-allowed"
          : active
          ? "bg-white/90 text-ink-0 shadow-sm"
          : "bg-black/40 text-white/80 hover:bg-black/60 hover:text-white"
      )}
    >
      {children}
    </button>
  );
}

// ── Tag strip ─────────────────────────────────────────────────────────────────

const MAX_VISIBLE_TAGS = 3;

function TagStrip({
  photo,
  onTagClick,
}: {
  photo: PhotoRecord;
  onTagClick?: (groupKey: string, tagValue: string) => void;
}) {
  const allTags = getAllTags(photo);
  if (allTags.length === 0) return null;

  const visible = allTags.slice(0, MAX_VISIBLE_TAGS);
  const overflow = allTags.length - visible.length;

  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {visible.map(({ value, group }, i) => (
        <TagChip
          key={i}
          label={value}
          onClick={onTagClick ? (e: React.MouseEvent) => {
            e.stopPropagation();
            onTagClick(group.key, value);
          } : undefined}
          clickable={Boolean(onTagClick)}
        />
      ))}
      {overflow > 0 && <TagOverflowChip count={overflow} />}
    </div>
  );
}

// ── Quick Tag Popover ─────────────────────────────────────────────────────────

const TAG_FIELD_MAP: Record<string, string> = {
  kimonoTags: "kimono_tag",
  kimonoColorTags: "kimono_color_tag",
  seasonTags: "season_tag",
  shootTags: "shoot_tag",
  sceneTags: "scene_tag",
};

function QuickTagPopover({
  photo,
  tagOptions,
  onSave,
  onClose,
}: {
  photo: PhotoRecord;
  tagOptions: Record<string, string[]>;
  onSave: (tags: Record<string, string[]>) => void;
  onClose: () => void;
}) {
  // Build current tag state from photo
  const [tagState, setTagState] = useState<Record<string, string[]>>(() => {
    const state: Record<string, string[]> = {};
    for (const group of TAG_GROUPS) {
      state[group.key] = [...(photo[group.key as keyof PhotoRecord] as string[] ?? [])];
    }
    return state;
  });

  // For adding custom tags
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [customInput, setCustomInput] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const addCustomTag = (groupKey: string) => {
    const trimmed = customInput.trim();
    if (trimmed) {
      setTagState((prev) => {
        const current = prev[groupKey] ?? [];
        if (!current.includes(trimmed)) {
          return { ...prev, [groupKey]: [...current, trimmed] };
        }
        return prev;
      });
    }
    setCustomInput("");
    setAddingFor(null);
    setError(null);
  };

  const toggleTag = (groupKey: string, value: string) => {
    setTagState((prev) => {
      const current = prev[groupKey] ?? [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [groupKey]: next };
    });
    setError(null);
  };

  const handleSave = () => {
    const result: Record<string, string[]> = {};
    for (const group of TAG_GROUPS) {
      const fieldName = TAG_FIELD_MAP[group.key];
      if (fieldName) {
        result[fieldName] = tagState[group.key] ?? [];
      }
    }
    // Trang phục + Màu sắc + Cảnh are mandatory (rule in @kra/photo-journal-shared).
    const missing = getMissingRequiredTagGroups(result);
    if (missing.length > 0) {
      setError(`Thiếu tag bắt buộc: ${missing.map((g) => g.label).join(", ")}`);
      return;
    }
    onSave(result);
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/40"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
      />

      {/* Bottom sheet */}
      <motion.div
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="fixed left-0 right-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-2xl max-h-[75vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-surface-3" />
        </div>

        {/* Title */}
        <div className="px-5 pb-3 border-b border-surface-2">
          <h3 className="text-base font-semibold text-ink-0 truncate">{photo.chatName}</h3>
        </div>

        {/* Scrollable tag content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {TAG_GROUPS.map((group) => {
            const options = tagOptions[group.fieldName] ?? [];
            const required = isRequiredTagGroup(group.key);
            // Required groups always render (so staff see the mandatory *);
            // optional groups with no options stay hidden.
            if (options.length === 0 && !required) return null;
            const selected = tagState[group.key] ?? [];
            const unfilled = required && selected.length === 0;

            return (
              <div key={group.key}>
                <p className={cn(
                  "text-xs font-semibold uppercase tracking-wider mb-2",
                  unfilled ? "text-[#c0392b]" : "text-ink-3"
                )}>
                  {group.emoji} {group.label}
                  {required && <span className="text-[#c0392b]"> *</span>}
                </p>
                <div className="flex flex-wrap gap-2">
                  {/* Existing options */}
                  {options.map((opt) => {
                    const isActive = selected.includes(opt);
                    return (
                      <button
                        key={opt}
                        onClick={() => toggleTag(group.key, opt)}
                        className={cn(
                          "text-sm px-3.5 py-1.5 rounded-full border transition-colors",
                          isActive
                            ? "bg-ink-0 text-white border-ink-0"
                            : "bg-white text-ink-1 border-surface-3 hover:border-ink-3"
                        )}
                      >
                        {isActive && "✓ "}{opt}
                      </button>
                    );
                  })}
                  {/* Custom tags (not in schema options) */}
                  {selected.filter((s) => !options.includes(s)).map((custom) => (
                    <button
                      key={custom}
                      onClick={() => toggleTag(group.key, custom)}
                      className="text-sm px-3.5 py-1.5 rounded-full border bg-ink-0 text-white border-ink-0 transition-colors"
                    >
                      ✓ {custom}
                    </button>
                  ))}
                  {/* Add custom tag */}
                  {addingFor === group.key ? (
                    <div className="flex items-center gap-1">
                      <input
                        ref={customInputRef}
                        autoFocus
                        value={customInput}
                        onChange={(e) => setCustomInput(e.target.value)}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") addCustomTag(group.key);
                          if (e.key === "Escape") { setAddingFor(null); setCustomInput(""); }
                        }}
                        placeholder="Nhập tag..."
                        className="text-sm px-3 py-1.5 rounded-full border border-[#B76E6E] bg-white outline-none w-28"
                      />
                      <button
                        onClick={() => addCustomTag(group.key)}
                        className="text-sm px-2.5 py-1.5 rounded-full bg-[#B76E6E] text-white"
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setAddingFor(group.key); setCustomInput(""); }}
                      className="text-sm px-3.5 py-1.5 rounded-full border border-dashed border-surface-3 text-ink-3 hover:border-ink-3 hover:text-ink-1 transition-colors"
                    >
                      + Thêm
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Required-tag error */}
        {error && (
          <p className="px-5 pt-2 text-xs text-[#c0392b] font-medium">{error}</p>
        )}

        {/* Footer buttons */}
        <div className="flex gap-3 px-5 py-4 border-t border-surface-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            onClick={onClose}
            className="flex-1 text-sm py-3 rounded-2xl text-ink-2 bg-surface-1 hover:bg-surface-2 font-medium"
          >
            Hủy
          </button>
          <button
            onClick={handleSave}
            className="flex-1 text-sm py-3 rounded-2xl bg-ink-0 text-white font-semibold hover:bg-[#23211e]"
          >
            Lưu tags
          </button>
        </div>
      </motion.div>
    </>
  );
}

// ── No-preview placeholder ────────────────────────────────────────────────────

function NoPreviewPlaceholder({ status }: { status: string }) {
  return (
    <div
      className="w-full bg-surface-2 flex items-center justify-center"
      style={{ aspectRatio: "3/4" }}
    >
      <div className="flex flex-col items-center gap-2 text-ink-3 px-4 text-center">
        <CameraIcon />
        <span className="text-2xs leading-tight">
          {status === "Processing"
            ? "Syncing…"
            : status === "Failed"
            ? "Sync failed"
            : "No preview"}
        </span>
      </div>
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      width={11}
      height={11}
      viewBox="0 0 24 24"
      fill={filled ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function ImportIconSmall() {
  return (
    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v13M7 11l5 5 5-5" />
      <path d="M20 21H4" />
    </svg>
  );
}

function BoltIconSmall() {
  return (
    <svg width={11} height={11} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12z" />
    </svg>
  );
}

function BookmarkIcon() {
  return (
    <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

// ── Inline rename title ──────────────────────────────────────────────────────

function InlineRenameTitle({
  value,
  onSave,
}: {
  value: string;
  onSave: (newName: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(value);
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }, [value]);

  const save = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) {
      onSave(trimmed);
    }
    setEditing(false);
  }, [draft, value, onSave]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") save();
          if (e.key === "Escape") setEditing(false);
        }}
        onClick={(e) => e.stopPropagation()}
        className="text-sm font-medium text-ink-0 leading-snug bg-transparent border-b border-[#B76E6E] outline-none w-full py-0"
      />
    );
  }

  return (
    <h3 className="text-sm font-medium text-ink-0 leading-snug truncate flex items-center gap-1 group/rename">
      <span className="truncate">{value}</span>
      <button
        onClick={startEdit}
        className="flex-shrink-0 p-0.5 rounded text-ink-3 opacity-0 group-hover/rename:opacity-100 hover:text-[#B76E6E] transition-all"
        title="Đổi tên"
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
        </svg>
      </button>
    </h3>
  );
}
